'use client';

import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';

/**
 * 错误提示 Modal 组件
 */
function ErrorModal({
  errorCount,
  onConfirm,
  onCancel,
}: {
  errorCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-700 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* 头部 */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-700 bg-zinc-800/50">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200">
            <AlertTriangle className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Multiple Errors Detected</h3>
            <p className="text-sm text-zinc-400">{errorCount} errors</p>
          </div>
          <button
            onClick={onCancel}
            className="absolute top-4 right-4 p-1 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 内容 */}
        <div className="px-6 py-5">
          <p className="text-zinc-300 mb-4">This may be caused by:</p>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
              Code syntax error
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
              Resource loading failure
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
              Cache issues
            </li>
          </ul>
        </div>

        {/* 按钮 */}
        <div className="flex gap-3 px-6 py-4 border-t border-zinc-700 bg-zinc-800/30">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-zinc-300 bg-zinc-700 hover:bg-zinc-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Clear Cache & Refresh
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * 开发环境错误追踪组件
 * 在开发模式下显示详细的错误信息和日志
 */
export function DevErrorTracker() {
  const [showModal, setShowModal] = useState(false);
  const [currentErrorCount, setCurrentErrorCount] = useState(0);

  const handleConfirm = useCallback(() => {
    // 清除所有缓存
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  }, []);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    // 通过 window 事件通知重置计数
    window.dispatchEvent(new CustomEvent('dev-error-reset'));
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    let errorCount = 0;
    const MAX_ERRORS = 5;
    let modalTimer: number | null = null;
    let disposed = false;

    // 监听重置事件
    const handleReset = () => {
      errorCount = 0;
      if (modalTimer !== null) {
        window.clearTimeout(modalTimer);
        modalTimer = null;
      }
    };
    window.addEventListener('dev-error-reset', handleReset);

    // 增强的错误处理
    const originalError = console.error;
    let inErrorHandler = false;
    console.error = (...args) => {
      if (inErrorHandler) return originalError.apply(console, args);
      inErrorHandler = true;
      try {
        originalError.apply(console, args);
        errorCount++;
        if (errorCount >= MAX_ERRORS && modalTimer === null) {
          const nextErrorCount = errorCount;
          modalTimer = window.setTimeout(() => {
            modalTimer = null;
            if (disposed) {
              return;
            }
            setCurrentErrorCount(nextErrorCount);
            setShowModal(true);
          }, 0);
        }
      } finally {
        inErrorHandler = false;
      }
    };

    // 监控长时间的 fetch 请求
    const originalFetch = window.fetch;
    const boundOriginalFetch = originalFetch.bind(window);
    const trackedFetch: typeof window.fetch = function (...args) {
      const startTime = Date.now();
      const input = args[0];
      const url = typeof input === 'string' 
        ? input 
        : input instanceof Request 
          ? input.url 
          : input.toString();

      const timeoutWarning = setTimeout(() => {
        console.warn(`[DevTracker] Slow request detected (>10s): ${url}`);
      }, 10000);

      return boundOriginalFetch(...args).finally(() => {
        clearTimeout(timeoutWarning);
        const duration = Date.now() - startTime;
        if (duration > 5000) {
          console.warn(`[DevTracker] Slow request completed (${duration}ms): ${url}`);
        }
      });
    };
    window.fetch = trackedFetch;

    return () => {
      disposed = true;
      if (modalTimer !== null) {
        window.clearTimeout(modalTimer);
      }
      console.error = originalError;
      if (window.fetch === trackedFetch) {
        window.fetch = originalFetch;
      }
      window.removeEventListener('dev-error-reset', handleReset);
    };
  }, []);

  return showModal ? (
    <ErrorModal
      errorCount={currentErrorCount}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  ) : null;
}
